import { useTheme } from "../../../contexts/theme";
import {DataGrid, GridRowId, GridRowSelectionModel} from "@mui/x-data-grid";
import {Paper} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useClipMamSelected} from "../../../contexts/clipMamSelected";
import ErrorSnackBar from "../../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import {clipGroupPageSize, mamTableColumns} from "../../../utils/constants"
import {Clip} from "../../../types/mamTypes";
import {mergeArraysWithoutDuplicates} from "../../../utils/utils";
import CustomPagination from "../../CommonComponents/CustomPagination/CustomPagination";


interface MamTableV2Props {
    clipsPlatform: Clip[]
    page: number;
    handlePage: (page: number) => void;
    loading: boolean;
}

export default function MamTableV2({clipsPlatform, page, handlePage, loading}: MamTableV2Props) {

    const theme = useTheme();
    const [errorMessage, setErrorMessage] = useState<{error: boolean, message: string}>({
        error:false,
        message: "",
    })
    const [previousPagesSelection, setPreviousPagesSelection] = useState<Clip[]>([]);
    const [currentPageSelection, setCurrentPageSelection] = useState<Clip[]>([]);

    const {aspectRatio, clips, setClipState} = useClipMamSelected();

    useEffect(() => {
        const tempPreviousPageSelection = [...previousPagesSelection]
        const existingIds = new Set(tempPreviousPageSelection.map(item => item.id));
        const mergedArray = [
            ...tempPreviousPageSelection,
            ...currentPageSelection.filter(item => !existingIds.has(item.id)) // Add only non-existing items
        ];
        setPreviousPagesSelection(mergedArray)
        setCurrentPageSelection([]);
    }, [page]);

    useEffect(() => {
        setPreviousPagesSelection([...clips])
    }, []);

    useEffect(() => {
        const tempCurrentPageSelection: Clip[] = []
        for (const socialClip of clipsPlatform){
            const contextClip = clips.find(clip => clip.id === socialClip.id);
            if (contextClip){
                tempCurrentPageSelection.push(contextClip);
            }
        }
        setCurrentPageSelection(tempCurrentPageSelection);
        setPreviousPagesSelection(previousPagesSelection.filter((clip) => !tempCurrentPageSelection.find(currentClip => currentClip.id === clip.id)));
    }, [clipsPlatform]);

    const findSocialClips = (rowSelectionModel : GridRowSelectionModel) => {
        return clipsPlatform.filter((clip: any) => {
            return rowSelectionModel.includes(clip.id);
        })
    }

    const checkSelectionAspectRatio = (socialClips: any[]) => {
        const legalAspectRatio = socialClips.every((socialClip) => socialClip.aspect_ratio === aspectRatio)
        if (!legalAspectRatio){
            setErrorMessage({
                error: true,
                message: "Clips with different aspect ratios cannot be selected.",
            });
        }
        return legalAspectRatio;
    }

    const handleRowSelectionChange = (rowSelectionModel: GridRowSelectionModel) => {
        let selectedIds = rowSelectionModel as GridRowId[]
        let socialClips = findSocialClips(selectedIds);
        if (checkSelectionAspectRatio(socialClips)){
            let selectionContextClips = [...clips]
            const clipsToAdd = socialClips.filter((actClip) => !clips.find(contextClip => contextClip.id === actClip.id))
            const clipsToDelete = selectionContextClips.filter((clip) => !selectedIds.includes(clip.id))
            selectionContextClips.push(...clipsToAdd)
            selectionContextClips = selectionContextClips.filter((contextClip) => !clipsToDelete.find(delClip => delClip.id === contextClip.id))
            const overallSelection = mergeArraysWithoutDuplicates(previousPagesSelection, selectionContextClips, 'id');
            setClipState(overallSelection);
            setCurrentPageSelection(selectionContextClips)
        }
    };

    return (
        <Paper
            sx={{
                height: 'auto',
                marginRight: `${theme.spacing.md}`,
                overflowY: 'auto',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '& .super-app-theme--header': {
                    backgroundColor: 'transparent',
                },
                paddingTop: theme.spacing.md
            }}
        >
            <ErrorSnackBar  autoHideDuration={2000} open={errorMessage.error} setClose={()=>{setErrorMessage({
                error: false,
                message: ""
            })}} errorMessage={errorMessage.message} />
            <DataGrid
                rows={clipsPlatform}
                columns={mamTableColumns}
                getRowId={(row) => row.id}
                hideFooter={true}
                checkboxSelection
                loading={loading}
                onRowSelectionModelChange={handleRowSelectionChange}
                rowSelectionModel={currentPageSelection.map(clip => clip.id)}
                sx={{
                    border: `2px solid ${theme.palette.white}`,
                    color: theme.palette.white,
                    backgroundColor: theme.palette.blue,
                    '& .MuiDataGrid-main': { backgroundColor: theme.palette.blue },
                    '& .MuiDataGrid-columnHeaders': { backgroundColor: theme.palette.blue },
                    '& .MuiDataGrid-columnHeader': { backgroundColor: theme.palette.blue },
                    '& .MuiDataGrid-row': { backgroundColor: theme.palette.blue },
                    '& .MuiDataGrid-footerContainer': { backgroundColor: theme.palette.blue },
                    '&.MuiDataGrid-root': {
                        '--DataGrid-containerBackground': theme.palette.blue,
                    },
                    '& .MuiTablePagination-root': {
                        color: theme.palette.white, // Override text color
                    },
                    '& .MuiTablePagination-selectIcon': {
                        color: theme.palette.white, // Change the color of the dropdown icon
                    },
                }}
            />
            <CustomPagination page={page} onPageChange={handlePage} disableArrowRight={clipsPlatform.length < clipGroupPageSize}/>
        </Paper>
    );
}